import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getFacilityMap, getMovementFlows } from "@/lib/warehouse-map.functions";
import { useScope } from "@/hooks/use-scope";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated/app/warehouse-map/movement")({
  component: MovementTab,
});

const KIND_COLORS: Record<string, string> = {
  receiving: "#3b82f6", storage: "#64748b", pick_face: "#10b981",
  packing: "#a855f7", returns: "#f59e0b", kitting: "#ec4899",
  staging: "#06b6d4", shipping: "#ef4444",
};

function MovementTab() {
  const { tenantId, facilityId } = useScope();
  const getMap = useServerFn(getFacilityMap);
  const getFlows = useServerFn(getMovementFlows);
  const [days, setDays] = useState(30);

  const { data: map } = useQuery({
    queryKey: ["wm-map", tenantId, facilityId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => getMap({ data: { tenantId: tenantId!, facilityId: facilityId! } }),
  });
  const { data: flows } = useQuery({
    queryKey: ["wm-flows", tenantId, facilityId, days],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => getFlows({ data: { tenantId: tenantId!, facilityId: facilityId!, days } }),
  });

  const cellPx = 18;
  const centroids = useMemo(() => {
    const out = new Map<string, { x: number; y: number }>();
    for (const z of map?.zones ?? []) {
      if (z.kind && z.grid_x != null && z.grid_y != null && z.grid_w && z.grid_h) {
        const c = out.get(z.kind);
        const cx = (z.grid_x + z.grid_w / 2) * cellPx;
        const cy = (z.grid_y + z.grid_h / 2) * cellPx;
        out.set(z.kind, c ? { x: (c.x + cx) / 2, y: (c.y + cy) / 2 } : { x: cx, y: cy });
      }
    }
    return out;
  }, [map]);

  const maxW = Math.max(1, ...(flows?.edges ?? []).map((e) => e.weight));

  if (!map) {
    return <div className="p-4 text-sm text-muted-foreground">Loading map…</div>;
  }
  const w = map.map.grid_cols * cellPx;
  const h = map.map.grid_rows * cellPx;

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">Aggregate flow between zone kinds.</div>
        <div className="flex gap-1">
          {[7, 30, 90].map((d) => (
            <Button key={d} size="sm" variant={days === d ? "default" : "outline"} onClick={() => setDays(d)}>{d}d</Button>
          ))}
        </div>
      </div>

      <div className="bg-muted/20 rounded-md border border-border overflow-auto">
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="block">
          <defs>
            <marker id="wmArrow" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto" markerUnits="strokeWidth">
              <path d="M0,0 L0,6 L9,3 z" fill="hsl(var(--foreground))" />
            </marker>
          </defs>
          {/* faint zones */}
          {(map.zones ?? []).filter((z) => z.grid_x != null && z.grid_y != null && z.grid_w && z.grid_h).map((z) => {
            const color = z.color ?? (z.kind ? KIND_COLORS[z.kind] : null) ?? "#64748b";
            return (
              <g key={z.id}>
                <rect x={z.grid_x! * cellPx} y={z.grid_y! * cellPx}
                  width={z.grid_w! * cellPx} height={z.grid_h! * cellPx}
                  fill={color} fillOpacity={0.1} stroke={color} strokeOpacity={0.4} />
                <text x={z.grid_x! * cellPx + 4} y={z.grid_y! * cellPx + 12}
                  fontSize="10" fill="hsl(var(--foreground))" fontFamily="ui-monospace,monospace" opacity={0.7}>
                  {z.code}
                </text>
              </g>
            );
          })}
          {/* arrows */}
          {(flows?.edges ?? []).map((e, i) => {
            const a = centroids.get(e.from);
            const b = centroids.get(e.to);
            if (!a || !b) return null;
            const sw = 1 + (e.weight / maxW) * 6;
            return (
              <g key={i}>
                <line x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                  stroke="hsl(var(--foreground))" strokeOpacity={0.75}
                  strokeWidth={sw} markerEnd="url(#wmArrow)" />
                <text x={(a.x + b.x) / 2} y={(a.y + b.y) / 2 - 4}
                  fontSize="10" textAnchor="middle" fill="hsl(var(--foreground))" fontFamily="ui-monospace,monospace">
                  {e.weight}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      <Card>
        <CardContent className="p-3 text-sm">
          <div className="font-medium mb-2">Edges</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {(flows?.edges ?? []).map((e, i) => (
              <div key={i} className="flex items-center justify-between border border-border rounded px-2 py-1 text-xs">
                <span className="capitalize">{e.from.replace("_"," ")} → {e.to.replace("_"," ")}</span>
                <span className="font-mono tabular-nums">{e.weight}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}